import { useState, useEffect } from 'react';
import { SDK } from '@gum-protocol/gpl-sdk';
import { PublicKey, Keypair } from "@solana/web3.js";
import { randomBytes } from 'crypto';
import { AES } from '@stablelib/aes';

export type UseSessionReturnType = {
  sessionToken: string | null,
  keypair: Keypair | null,
  encryptionKey: string,
  error: string | null,
  createSession: (targetProgram: PublicKey, owner: PublicKey, topUp?: boolean, expiry?: number | null)
    => Promise<{ sessionToken: string; keypair: Keypair; encryptionKey: string; } | undefined>
};

// Constants
const SESSION_STORAGE_KEY = 'q3s1m8t9-session-tkn';
const ENCRYPTION_KEY_STORAGE_KEY = 'd8b5n6m2-id';
const ENCRYPTION_KEY_LENGTH = 32; // 256 bits
const EXPIRY_TIME_IN_MS = 1000 * 60 * 60; // 1 hour

const useSession = (sdk: SDK): UseSessionReturnType => {
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [keypair, setKeypair] = useState<Keypair | null>(null);
  const [encryptionKey, setEncryptionKey] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const getSessionData = async () => {
      const sessionDataString = localStorage.getItem(SESSION_STORAGE_KEY);
      const encryptionKeyDataString = localStorage.getItem(ENCRYPTION_KEY_STORAGE_KEY);

      const sessionData = sessionDataString ? JSON.parse(sessionDataString) : null;
      const encryptionKeyData = encryptionKeyDataString ? JSON.parse(encryptionKeyDataString) : null;

      if (sessionData && encryptionKeyData) {
        const { encryptedToken, encryptedKeypair, expiry } = sessionData;
        const { encryptionKey: storedEncryptionKey, expiry: encryptionKeyExpiry } = encryptionKeyData;

        if (Date.now() > encryptionKeyExpiry || Date.now() > expiry) {
          localStorage.removeItem(ENCRYPTION_KEY_STORAGE_KEY);
          localStorage.removeItem(SESSION_STORAGE_KEY);
          return;
        }

        try {
          const decryptedToken = decrypt(encryptedToken, storedEncryptionKey);
          const decryptedKeypair = decrypt(encryptedKeypair, storedEncryptionKey);

          const keypair = Keypair.fromSecretKey(new Uint8Array(Buffer.from(decryptedKeypair, 'base64')));

          setSessionToken(decryptedToken);
          setKeypair(keypair);
          setEncryptionKey(storedEncryptionKey);
        } catch (error) {
          console.error("Error decrypting session data:", error);
          setError("Error decrypting session data");
        }
      }
    };

    getSessionData();
  }, []);

  const createSession = async (targetProgram: PublicKey, owner: PublicKey, topUp = false, expiry?: number | null) => {
    try {
      // if the expiry param is null or undefined, then the session will expire in 1 hour
      expiry = expiry || Date.now() + EXPIRY_TIME_IN_MS;

      const { sessionPDA: sessionToken, sessionSigner: keypair } = await sdk.session.create(targetProgram, owner, topUp, expiry);

      const encryptionKey = generateEncryptionKey();

      const sessionTokenString = sessionToken.toBase58();
      const keypairSecretBase64String = Buffer.from(keypair.secretKey).toString('base64');

      const encryptedToken = encrypt(sessionTokenString, encryptionKey);
      const encryptedKeypair = encrypt(keypairSecretBase64String, encryptionKey);

      localStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({ encryptedToken, encryptedKeypair, expiry })
      );
      localStorage.setItem(ENCRYPTION_KEY_STORAGE_KEY, JSON.stringify({ encryptionKey, expiry }));

      setSessionToken(sessionTokenString);
      setKeypair(keypair);
      setEncryptionKey(encryptionKey);
      return {
        sessionToken: sessionTokenString,
        keypair,
        encryptionKey,
      };
    } catch (error) {
      console.error("Error creating session:", error);
      setError("Error creating session");
      return undefined;
    }
  };

  return { sessionToken, keypair, encryptionKey, error, createSession};
};

function generateEncryptionKey(): string {
  const key = randomBytes(ENCRYPTION_KEY_LENGTH);
  return key.toString("base64");
}

function encrypt(data: string, key: string): string {
  const aes = new AES(new Uint8Array(Buffer.from(key, "base64")));

  const plaintext = new Uint8Array(new TextEncoder().encode(data));
  const ciphertext = new Uint8Array(plaintext.length);

  // Encrypt block by block
  for (let i = 0; i < plaintext.length; i += aes.blockSize) {
    aes.encryptBlock(
      plaintext.slice(i, i + aes.blockSize),
      ciphertext.subarray(i, i + aes.blockSize)
    );
  }

  return Buffer.from(ciphertext).toString("base64");
}

function decrypt(data: string, key: string): string {
  const aes = new AES(new Uint8Array(Buffer.from(key, "base64")));

  const ciphertext = new Uint8Array(Buffer.from(data, "base64"));
  const plaintext = new Uint8Array(ciphertext.length);

  // Decrypt block by block
  for (let i = 0; i < ciphertext.length; i += aes.blockSize) {
    aes.decryptBlock(
      ciphertext.slice(i, i + aes.blockSize),
      plaintext.subarray(i, i + aes.blockSize)
    );
  }

  return new TextDecoder().decode(plaintext);
}

export default useSession;
